/**
 * Copyright (c) 2026, CheckPlus Limited
 */
/**
 * Subdomain Routes
 */
import React from "react";
import PropTypes from "prop-types";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { Helmet } from "react-helmet";
import PublicRoute from "./PublicRoute";
import ProtectedRoute from "./ProtectedRoute";
import LoginRoute from "./LoginRoute";

import StripeRoute from "./StripeRoute";
import { AuthProvider } from "./AuthContext";
import { readOrgTitle } from "./assets/orgTitle";

/* Payment flow */
import SelectMission from "./app/SelectMission/SelectMission";
import MissionDetails from "./app/MissionDetails/MissionDetails";
import ExpressPayment from "./app/ExpressPayment/ExpressPayment";
import PersonalDetails from "./app/PersonalDetails/PersonalDetails";
import SelectPayment from "./app/SelectPayment/SelectPayment";
import CardPrompt from "./app/CardPrompt/CardPrompt";
import Review from "./app/Review/Review";
import ConfirmPayment from "./app/ConfirmPayment/ConfirmPayment";

/* HSBC PayMe */
import PayMeLink from "./app/PayMeLink/PayMeLink";
import PayMeCode from "./app/PayMeCode/PayMeCode";
import ErrorPromptPayMe from "./app/ErrorPromptPayMe/ErrorPromptPayMe";

/* HKT Tap & Go */
import TapAndGoCode from "./app/TapAndGoCode/TapAndGoCode";
import ErrorPromptTapAndGo from "./app/ErrorPromptTapAndGo/ErrorPromptTapAndGo";

/* Manual Payments */
import ManualPayments from "./app/ManualPayments/ManualPayments";
import ReceiptUpload from "./app/ReceiptUpload/ReceiptUpload";

/* Payer Account */
import LoginPrompt from "./app/LoginPrompt/LoginPrompt";
import LoginFaq from "./app/LoginFaq/LoginFaq";
import LoginEmail from "./app/LoginEmail/LoginEmail";
import LoginPassword from "./app/LoginPassword/LoginPassword";
import SignupPassword from "./app/SignupPassword/SignupPassword";
import SignupPasswordAgain from "./app/SignupPasswordAgain/SignupPasswordAgain";
import SignupInfo from "./app/SignupInfo/SignupInfo";
import EmailVerification from "./app/EmailVerification/EmailVerification";
import VerifyCode from "./app/VerifyCode/VerifyCode";
import ForgotPassword from "./app/ForgotPassword/ForgotPassword";
import ResetPassword from "./app/ResetPassword/ResetPassword";
import ChangePassword from "./app/ChangePassword/ChangePassword";
import NewPassword from "./app/NewPassword/NewPassword";
import RePassword from "./app/RePassword/RePassword";
import ConfirmPassword from "./app/ConfirmPassword/ConfirmPassword";
import AcInfo from "./app/AcInfo/AcInfo";
import AcInfoEdit from "./app/AcInfoEdit/AcInfoEdit";
import DeleteAccount from "./app/DeleteAccount/DeleteAccount";
import ListCards from "./app/ListCards/ListCards";
import AddCard from "./app/AddCard/AddCard";
import DonationInfoProcessed from "./app/DonationInfoProcessed/DonationInfoProcessed";
import DonationInfoEdit from "./app/DonationInfoEdit/DonationInfoEdit";
import DonationInfoRecurring from "./app/DonationInfoRecurring/DonationInfoRecurring";
import DonationInfoReauthenticate from "./app/DonationInfoReauthenticate/DonationInfoReauthenticate";
import Transactions from "./app/Transactions/Transactions";
import LegacyTransactionsRedirect from "./app/Transactions/LegacyTransactionsRedirect";

import EmailVerified from "./app/EmailVerified/EmailVerified";
import DonorEmailVerified from "./app/DonorEmailVerified/DonorEmailVerified";
import ResetEmail from "./app/ResetEmail/ResetEmail";
import UsernameConfirm from "./app/UsernameConfirm/UsernameConfirm";

/* Generic system response pages */
import ErrorPrompt from "./app/ErrorPrompt/ErrorPrompt";
import DonePrompt from "./app/DonePrompt/DonePrompt";

const Routes = ({ component: Component, ...rest }) => (
  <BrowserRouter>
    <AuthProvider>
      {/* The organisation's name as every donor tab's default title. Helmet
          falls back to it whenever a page's own title is empty (before its
          fetch answers) or the page sets none. A pathless Route re-renders
          on each navigation, so a name remembered on one page reaches the
          next. See src/assets/orgTitle.js. */}
      <Route render={() => <Helmet defaultTitle={readOrgTitle()} />} />
      <Switch>
        <PublicRoute exact path="/" component={SelectMission} />

        {/* Payment flow */}
        <PublicRoute path="/SelectMission" component={SelectMission} />
        <StripeRoute path="/MissionDetails" component={MissionDetails} />
        <StripeRoute path="/ExpressPayment" component={ExpressPayment} />
        <StripeRoute path="/SelectPayment" component={SelectPayment} />
        <StripeRoute path="/CardPrompt" component={CardPrompt} />
        <PublicRoute path="/PersonalDetails" component={PersonalDetails} />
        <StripeRoute path="/Review" component={Review} />
        <PublicRoute path="/ConfirmPayment" component={ConfirmPayment} />

        {/* HSBC PayMe */}
        <PublicRoute path="/PayMeLink" component={PayMeLink} />
        <PublicRoute path="/PayMeCode" component={PayMeCode} />

        {/* Homepages in Payer Account */}
        <LoginRoute path="/LoginPrompt" component={LoginPrompt} />
        <LoginRoute path="/LoginFaq" component={LoginFaq} />
        <LoginRoute path="/LoginEmail" component={LoginEmail} />
        <LoginRoute path="/LoginPassword" component={LoginPassword} />
        <LoginRoute path="/SignupPassword" component={SignupPassword} />
        <LoginRoute
          path="/SignupPasswordAgain"
          component={SignupPasswordAgain}
        />
        <LoginRoute path="/SignupInfo" component={SignupInfo} />
        <LoginRoute path="/EmailVerification" component={EmailVerification} />
        <LoginRoute path="/VerifyCode" component={VerifyCode} />
        <LoginRoute path="/ForgotPassword" component={ForgotPassword} />
        <LoginRoute path="/ResetPassword" component={ResetPassword} />
        <PublicRoute path="/UsernameConfirm" component={UsernameConfirm} />
        <PublicRoute path="/EmailVerified" component={EmailVerified} />
        <PublicRoute
          path="/DonorEmailVerified"
          component={DonorEmailVerified}
        />
        <PublicRoute path="/ResetEmail" component={ResetEmail} />

        <ProtectedRoute
          path="/DonationInfoProcessed"
          component={DonationInfoProcessed}
        />
        <ProtectedRoute path="/DonationInfoEdit" component={DonationInfoEdit} />
        <StripeRoute
          path="/DonationInfoRecurring"
          component={DonationInfoRecurring}
          requireAuth={true}
        />
        <StripeRoute
          path="/DonationInfoReauthenticate"
          component={DonationInfoReauthenticate}
          requireAuth={true}
        />
        <ProtectedRoute path="/Transactions/:tab?" component={Transactions} />
        {/* The tabs' old pages, kept for bookmarks (CP-413) */}
        <Route path="/DonationRecord" component={LegacyTransactionsRedirect} />
        <Route
          path="/DonationRecordReauthenticate"
          component={LegacyTransactionsRedirect}
        />
        <Route
          path="/DonationRecordRecurring"
          component={LegacyTransactionsRedirect}
        />

        <ProtectedRoute path="/ChangePassword" component={ChangePassword} />
        <ProtectedRoute path="/NewPassword" component={NewPassword} />
        <ProtectedRoute path="/RePassword" component={RePassword} />
        <PublicRoute path="/ConfirmPassword" component={ConfirmPassword} />

        <ProtectedRoute path="/AcInfo" component={AcInfo} />
        <ProtectedRoute path="/AcInfoEdit" component={AcInfoEdit} />
        <ProtectedRoute path="/DeleteAccount" component={DeleteAccount} />
        <ProtectedRoute path="/ListCards" component={ListCards} />
        <StripeRoute path="/AddCard" component={AddCard} />

        <PublicRoute
          path="/ErrorPromptPayMe"
          exact={true}
          component={ErrorPromptPayMe}
        />

        {/* HKT Tap & Go */}

        <PublicRoute
          path="/TapAndGoCode"
          exact={true}
          component={TapAndGoCode}
        />

        <PublicRoute
          path="/ErrorPromptTapAndGo"
          exact={true}
          component={ErrorPromptTapAndGo}
        />

        {/* Manual Payments */}

        <PublicRoute
          path="/ManualPayments"
          exact={true}
          component={ManualPayments}
        />

        <PublicRoute
          path="/ReceiptUpload"
          exact={true}
          component={ReceiptUpload}
        />

        {/* Generic system response pages */}
        <PublicRoute path="/ErrorPrompt" component={ErrorPrompt} />
        <PublicRoute path="/DonePrompt" component={DonePrompt} />
        <PublicRoute path="/404" component={ErrorPrompt} />
        <PublicRoute path="*" exact={true} component={ErrorPrompt} />
      </Switch>
    </AuthProvider>
  </BrowserRouter>
);

Routes.propTypes = {
  component: PropTypes.func,
};

export default Routes;
